import { useState, type FormEvent } from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import PageTransition from "../components/PageTransition";
import PageHero from "../components/PageHero";
import { useTranslation } from "../i18n/LanguageContext";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const t = useTranslation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const message = formData.get("message") as string;
    if (message && message.length > 500) { setStatus("error"); return; }

    setStatus("sending");
    try {
      const data = new URLSearchParams();
      formData.forEach((val, key) => { if (typeof val === "string") data.append(key, val); });
      const res = await fetch("https://readdy.ai/api/form/d7b1418i2a4mi6nvdvrg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setStatus(res.ok ? "success" : "error");
      if (res.ok) form.reset();
    } catch {
      setStatus("error");
    }
  };

  const inputBase = "w-full bg-transparent border-b border-border h-12 text-[15px] text-primary placeholder:text-secondary/40 focus:outline-none focus:border-accent transition-colors duration-300";

  return (
    <PageTransition>
      <PageHero title={t.contact_title} description={t.contact_description} />

      <section className="py-24 md:py-32">
        <div className="max-w-content mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-5 gap-20">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-heading-sm md:text-[36px] mb-10">Parlons de votre projet</h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-accent mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <p className="text-secondary text-[15px] leading-relaxed">
                    08, av. Savonnier, Bel-Air<br />Kampemba, Lubumbashi<br />Haut-Katanga, RDC
                  </p>
                </div>
                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-accent mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="text-secondary text-[15px]">
                    <p>+(243) 993 800 679</p>
                    <p>+(243) 890 028 426</p>
                    <p className="mt-3 pt-3 border-t border-border">+1 (438) 227-0788 <span className="text-secondary/50">(Canada)</span></p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-accent mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  <div className="text-secondary text-[15px]">
                    <p>contact@ravaservices.net</p>
                    <p>info@ravaservices.net</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-10 border-t border-border">
                <div className="h-52 bg-[#F3F4F6]">
                  <iframe
                    title="RAVA"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15837.012!2d27.4667!3d-11.6600!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x19723d2d3e3a6a73%3A0x6bcd7fcf69c1d4d0!2sKampemba%2C+Lubumbashi%2C+Congo+%E2%80%94+Kinshasa!5e0!3m2!1sfr!2sfr!4v1712000000000"
                    width="100%" height="100%" style={{ border: 0 }}
                    allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <h3 className="text-[12px] uppercase tracking-[0.1em] font-semibold text-primary mb-2">{t.contact_form_title}</h3>
              <p className="text-secondary text-[15px] mb-10">{t.contact_form_subtitle}</p>

              {status === "success" ? (
                <div className="border border-border p-16 text-center">
                  <h4 className="font-serif text-2xl mb-3">{t.contact_success_title}</h4>
                  <p className="text-secondary text-[15px] mb-8">{t.contact_success_text}</p>
                  <button onClick={() => setStatus("idle")} className="text-[13px] font-semibold text-accent hover:text-accent-dark transition-colors duration-300">
                    {t.contact_success_again}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">
                        {t.contact_name} <span className="text-accent">{t.contact_required}</span>
                      </label>
                      <input type="text" name="nom" required placeholder="Jean Dupont" className={inputBase} />
                    </div>
                    <div>
                      <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">
                        {t.contact_email} <span className="text-accent">{t.contact_required}</span>
                      </label>
                      <input type="email" name="email" required placeholder="votre@email.com" className={inputBase} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">{t.contact_phone}</label>
                      <input type="tel" name="telephone" placeholder="+243 xxx xxx xxx" className={inputBase} />
                    </div>
                    <div>
                      <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">{t.contact_budget}</label>
                      <select name="budget" className={`${inputBase} cursor-pointer bg-white`}>
                        <option value="">{t.contact_budget_select}</option>
                        <option value="< 10 000 $">{t.contact_budget_10}</option>
                        <option value="10 000 - 50 000 $">{t.contact_budget_50}</option>
                        <option value="50 000 - 200 000 $">{t.contact_budget_200}</option>
                        <option value="> 200 000 $">{t.contact_budget_plus}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">
                      {t.contact_service} <span className="text-accent">{t.contact_required}</span>
                    </label>
                    <select name="service" required className={`${inputBase} cursor-pointer bg-white`}>
                      <option value="">{t.contact_service_select}</option>
                      <option value="Construction">{t.svc_construction_title}</option>
                      <option value="Travaux Publics">{t.svc_tp_title}</option>
                      <option value="Transport & Logistique">{t.svc_logistique_title}</option>
                      <option value="Santé & Sécurité SST">{t.svc_sst_title}</option>
                      <option value="Solutions Digitales & IA">{t.svc_digital_title}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[13px] uppercase tracking-[0.05em] font-medium text-primary mb-3">
                      {t.contact_message} <span className="text-accent">{t.contact_required}</span>
                    </label>
                    <textarea name="message" required maxLength={500} rows={5} placeholder={t.contact_message_placeholder}
                      className="w-full border border-border bg-transparent p-4 text-[15px] text-primary placeholder:text-secondary/40 focus:outline-none focus:border-accent transition-colors duration-300 resize-none" />
                  </div>
                  {status === "error" && <p className="text-accent text-[14px]">{t.contact_error}</p>}
                  <button type="submit" disabled={status === "sending"}
                    className="w-full text-[13px] uppercase tracking-[0.08em] font-semibold text-white bg-accent h-14 hover:bg-accent-dark transition-colors duration-300 disabled:opacity-50">
                    {status === "sending" ? t.contact_sending : t.contact_submit}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
